import { useCallback, useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { useProjectList } from '@/modules/projects/shared/useProjectList';
import type { ProjectAnalytics, ProjectPortfolio } from '@/types/projects';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function ProjectsPortfolioPage() {
  const { projects } = useProjectList();
  const portfoliosPagination = usePagination();
  const [analytics, setAnalytics] = useState<ProjectAnalytics | null>(null);
  const [portfolios, setPortfolios] = useState<ProjectPortfolio[]>([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(false);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [budget, setBudget] = useState<{ budgetAmount: number; actualCost: number; variance: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ portfolioCode: '', portfolioName: '', description: '' });

  const loadPortfolios = useCallback(() => {
    setPortfoliosLoading(true);
    projectsApi
      .listPortfolios({ page: portfoliosPagination.page, size: portfoliosPagination.size })
      .then((r) => {
        setPortfolios(r.content);
        portfoliosPagination.applyPageResponse(r);
      })
      .catch(() => undefined)
      .finally(() => setPortfoliosLoading(false));
  }, [portfoliosPagination.page, portfoliosPagination.size, portfoliosPagination]);

  useEffect(() => {
    projectsApi.analyticsDashboard().then(setAnalytics).catch(() => undefined);
  }, []);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  useEffect(() => {
    if (!projectId) { setBudget(null); return; }
    projectsApi.budgetSummary(projectId).then(setBudget).catch((e) => showApiError(e, 'Budget load failed'));
  }, [projectId]);

  const createPortfolio = async () => {
    try {
      await projectsApi.createPortfolio(form);
      notify.success('Portfolio created');
      setOpen(false);
      portfoliosPagination.resetPage();
      loadPortfolios();
    } catch (err) {
      showApiError(err, 'Create failed');
    }
  };

  return (
    <div>
      <PageHeader title="PMO & Portfolio" description="Executive dashboard, portfolios, and project budget health"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Portfolio' }]}
        actions={<Button onClick={() => setOpen(true)}>New portfolio</Button>} />
      {analytics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="card p-4"><p className="text-sm text-slate-500">Active projects</p><p className="text-2xl font-semibold">{analytics.activeProjects}</p></div>
          <div className="card p-4"><p className="text-sm text-slate-500">Task completion</p><p className="text-2xl font-semibold">{analytics.taskCompletionRate.toFixed(0)}%</p></div>
          <div className="card p-4"><p className="text-sm text-slate-500">Open risks</p><p className="text-2xl font-semibold">{analytics.openRisks}</p></div>
          <div className="card p-4"><p className="text-sm text-slate-500">Sprint velocity</p><p className="text-2xl font-semibold">{analytics.sprintVelocity}</p></div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Portfolios</h3>
          <ul className="space-y-2">{portfolios.map((p) => <li key={p.id} className="text-sm border-b pb-2"><strong>{p.portfolioName}</strong> — {p.portfolioCode}</li>)}</ul>
          <TablePagination
            page={portfoliosPagination.page}
            totalPages={portfoliosPagination.totalPages}
            totalElements={portfoliosPagination.totalElements}
            onPageChange={portfoliosPagination.setPage}
            loading={portfoliosLoading}
            hasRows={portfolios.length > 0}
          />
        </div>
        <div className="card p-4">
          <h3 className="font-semibold mb-3">Project budget</h3>
          <Select
            className="mb-3"
            placeholder="Select project"
            value={String(projectId ?? '')}
            onChange={(v) => setProjectId(v ? Number(v) : undefined)}
            options={projects.map((p) => ({ value: String(p.id), label: p.projectName }))}
          />
          {budget && (
            <dl className="text-sm space-y-1">
              <div className="flex justify-between"><dt>Budget</dt><dd>{budget.budgetAmount}</dd></div>
              <div className="flex justify-between"><dt>Actual (MTD)</dt><dd>{budget.actualCost}</dd></div>
              <div className="flex justify-between"><dt>Variance</dt><dd>{budget.variance}</dd></div>
            </dl>
          )}
        </div>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title="Create portfolio" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void createPortfolio()}>Save</Button></>}>
        <div className="space-y-3">
          <Input label="Code" value={form.portfolioCode} onChange={(e) => setForm({ ...form, portfolioCode: e.target.value })} />
          <Input label="Name" value={form.portfolioName} onChange={(e) => setForm({ ...form, portfolioName: e.target.value })} />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
